import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Field, useFormikContext } from 'formik';
import { Button, useModal, useToastr } from '@farmshare/ui-components';

import type { PrimalGroup } from 'lib/graphql';
import { UploadImageModal } from '../components/upload-image-modal';

export function CutDescriptionsSection() {
  const [uploadModalPrimal, setUploadModalPrimal] = useState<{
    groupIndex: number;
    primalIndex: number;
  } | null>(null);
  const { ask } = useModal();
  const { push } = useToastr();

  const { values, setFieldValue } = useFormikContext<{
    primalGroups: PrimalGroup[];
  }>();
  const primalGroups = values.primalGroups || [];

  return (
    <Accordion
      sx={{
        mb: 2,
        '&:before': {
          display: 'none',
        },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&.Mui-expanded': {
          my: '16px !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
        sx={{
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.02)',
          },
          borderRadius: '8px',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography variant="h6">Cut Descriptions</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add descriptions for primals and their cuts that will be shown to
          customers in the wizard.
        </Typography>
        <Stack spacing={3}>
          {primalGroups.map((group, groupIndex) => (
            <Box key={group._id}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
              >
                {group.name}
              </Typography>
              <Stack spacing={2}>
                {group.primals?.map((primal, primalIndex) => {
                  if (!primal) return null; // GraphQL arrays may contain null elements
                  if (!primal.cuts || primal.cuts.length === 0) return null;

                  return (
                    <Box
                      key={primal._id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        p: 2.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: 'text.primary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {primal.name}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'stretch',
                          height: '100%',
                        }}
                      >
                        {/* Image preview or Add Image button */}
                        <Box
                          sx={{
                            position: 'relative',
                            width: 120,
                            minWidth: 120,
                            border: 1,
                            borderColor: 'divider',
                            borderStyle:
                              (primal as any).image || (primal as any).imageUrl
                                ? 'solid'
                                : 'dashed',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            bgcolor: 'background.paper',
                          }}
                        >
                          {(primal as any).image || (primal as any).imageUrl ? (
                            <>
                              <img
                                src={
                                  (primal as any).image
                                    ? URL.createObjectURL((primal as any).image)
                                    : (primal as any).imageUrl
                                }
                                alt={primal.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  right: 4,
                                  display: 'flex',
                                  gap: 0.5,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUploadModalPrimal({
                                      groupIndex,
                                      primalIndex,
                                    });
                                  }}
                                  sx={{
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                      bgcolor: 'action.hover',
                                    },
                                    width: 28,
                                    height: 28,
                                    p: 0.5,
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    ask({
                                      type: 'ask',
                                      title: 'Remove Image',
                                      body: `Are you sure you want to remove the image for ${primal.name}?`,
                                      onConfirm: async () => {
                                        try {
                                          // Mark image for removal
                                          await setFieldValue(
                                            `primalGroups.${groupIndex}.primals.${primalIndex}.imageToRemove`,
                                            true,
                                          );
                                          // Clear image and imageUrl
                                          await setFieldValue(
                                            `primalGroups.${groupIndex}.primals.${primalIndex}.image`,
                                            undefined,
                                          );
                                          await setFieldValue(
                                            `primalGroups.${groupIndex}.primals.${primalIndex}.imageUrl`,
                                            undefined,
                                          );
                                        } catch (error) {
                                          push({
                                            title: 'Error',
                                            body:
                                              error instanceof Error
                                                ? error.message
                                                : 'Failed to remove image',
                                            severity: 'error',
                                            delay: 5000,
                                          });
                                        }
                                      },
                                    });
                                  }}
                                  sx={{
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                      bgcolor: 'error.light',
                                      color: 'error.contrastText',
                                    },
                                    width: 28,
                                    height: 28,
                                    p: 0.5,
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            <Button
                              onClick={() => {
                                setUploadModalPrimal({
                                  groupIndex,
                                  primalIndex,
                                });
                              }}
                              variant="outline-primary"
                              color="primary"
                              content="Add Image"
                              sx={{
                                m: 0,
                                borderStyle: 'dashed',
                                width: '100%',
                                height: '100%',
                                minHeight: '100%',
                              }}
                            />
                          )}
                        </Box>
                        <Field
                          name={`primalGroups.${groupIndex}.primals.${primalIndex}.description`}
                        >
                          {({ field }: any) => (
                            <TextField
                              {...field}
                              value={field.value ?? ''}
                              fullWidth
                              multiline
                              rows={2}
                              label="Category Description"
                              placeholder={`Describe ${primal.name}...`}
                              helperText="Describe this cut category for customers"
                            />
                          )}
                        </Field>
                      </Box>

                      {primal.cuts && primal.cuts.length > 0 && (
                        <Box sx={{ pl: 3, mt: 2 }}>
                          <Stack spacing={2}>
                            {primal.cuts.map((cut, cutIndex) => (
                              <Field
                                key={cut._id}
                                name={`primalGroups.${groupIndex}.primals.${primalIndex}.cuts.${cutIndex}.description`}
                              >
                                {({ field }: any) => (
                                  <TextField
                                    {...field}
                                    value={field.value ?? ''}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    label={cut.name}
                                    placeholder={`Describe ${cut.name}...`}
                                    helperText="Help customers understand this cut"
                                  />
                                )}
                              </Field>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
      {uploadModalPrimal !== null && (
        <UploadImageModal
          open
          onClose={() => setUploadModalPrimal(null)}
          specificationIndex={0}
          currentImageUrl={
            (
              primalGroups[uploadModalPrimal.groupIndex]?.primals?.[
                uploadModalPrimal.primalIndex
              ] as any
            )?.imageUrl ||
            ((
              primalGroups[uploadModalPrimal.groupIndex]?.primals?.[
                uploadModalPrimal.primalIndex
              ] as any
            )?.image
              ? URL.createObjectURL(
                  (
                    primalGroups[uploadModalPrimal.groupIndex]?.primals?.[
                      uploadModalPrimal.primalIndex
                    ] as any
                  ).image,
                )
              : undefined)
          }
          onImageSelected={async (file: File) => {
            try {
              await setFieldValue(
                `primalGroups.${uploadModalPrimal.groupIndex}.primals.${uploadModalPrimal.primalIndex}.image`,
                file,
              );
              // Clear imageToRemove flag if set
              await setFieldValue(
                `primalGroups.${uploadModalPrimal.groupIndex}.primals.${uploadModalPrimal.primalIndex}.imageToRemove`,
                false,
              );
            } catch (error) {
              push({
                title: 'Error',
                body:
                  error instanceof Error
                    ? error.message
                    : 'Failed to set image',
                severity: 'error',
                delay: 5000,
              });
            }
          }}
        />
      )}
    </Accordion>
  );
}
